import { useTranslation } from "react-i18next"

import { FileText, Paperclip, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

import { type StartField } from "../utils/workflow-run-utils"
import { type TestRunUserInputNode } from "../utils/workflow-test-run-user-inputs"

type FileMode = "none" | "optional" | "required"

type WorkflowTestPanelInputsProps = {
  /** When true, render the dynamic multi-field form; otherwise the legacy branch. */
  useFieldForm: boolean
  /** Text fields resolved from the start node (drives the dynamic form). */
  textFields: StartField[]
  /**
   * Per-text-field values keyed by each field's key. Entries are absent until the
   * user types, so values are optional (justifies the ?./?? guards on read).
   */
  fieldTextValues: Record<string, string | undefined>
  /** Free-form input value for the legacy single-input path. */
  inputValue: string
  /** Whether a test run is in progress (disables inputs). */
  isRunning: boolean
  /** Legacy flag: show the single free-form text input. */
  allowTextInput: boolean
  /** Legacy flag: show the file upload controls. */
  allowFiles: boolean
  /** File upload mode (drives the legacy Files label). */
  fileMode: FileMode
  /** Whether the start node declares a file field (dynamic form). */
  hasFileField: boolean
  /** Currently selected files (shared upload pipeline). */
  files: File[]
  /** Mid-flow User Input nodes that need an answer up front (#2964). */
  userInputNodes: TestRunUserInputNode[]
  /** Answers keyed by each User Input node's inputId; absent until typed. */
  userInputAnswers: Record<string, string | undefined>
  handleUserInputAnswerChange: (inputId: string, value: string) => void
  handleFieldTextChange: (key: string, value: string) => void
  setInputValue: (value: string) => void
  handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void
  setFilePickerOpen: (open: boolean) => void
  removeFile: (index: number) => void
}

export function WorkflowTestPanelInputs({
  useFieldForm,
  textFields,
  fieldTextValues,
  inputValue,
  isRunning,
  allowTextInput,
  allowFiles,
  fileMode,
  hasFileField,
  files,
  userInputNodes,
  userInputAnswers,
  handleUserInputAnswerChange,
  handleFieldTextChange,
  setInputValue,
  handleKeyDown,
  setFilePickerOpen,
  removeFile,
}: WorkflowTestPanelInputsProps) {
  const { t } = useTranslation()

  /* #2964: Test Run streams through the Explorer chat path and cannot pause at
     a User Input node, so its answer is asked for here, before the run, and
     sent under the node's inputId. Rendered by both branches below. */
  const userInputSection =
    userInputNodes.length > 0 ? (
      <div data-testid="test-run-user-input-answers">
        <p className="text-muted-foreground mb-1 text-xs font-medium">
          {t("workflowLanding.testPanel.userInputAnswers.title", "Answers for User Input nodes")}
        </p>
        <p className="text-muted-foreground mb-2 text-[11px]">
          {t(
            "workflowLanding.testPanel.userInputAnswers.hint",
            "Test Run does not pause at User Input nodes. The answers you enter here are used when each node runs."
          )}
        </p>
        <div className="space-y-3">
          {userInputNodes.map((node) => (
            <div key={node.nodeId}>
              <label
                className="text-muted-foreground mb-1.5 block text-xs font-medium"
                htmlFor={`test-run-user-input-${node.nodeId}`}
              >
                {node.prompt ||
                  t("workflowLanding.testPanel.userInputAnswers.unnamed", "User input")}
              </label>
              <Textarea
                className="min-h-[60px] resize-none text-sm"
                disabled={isRunning}
                id={`test-run-user-input-${node.nodeId}`}
                value={userInputAnswers[node.inputId] ?? ""}
                placeholder={t(
                  "workflowLanding.testPanel.userInputAnswers.placeholder",
                  "Answer for this node…"
                )}
                onChange={(e) => handleUserInputAnswerChange(node.inputId, e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
          ))}
        </div>
      </div>
    ) : null

  if (useFieldForm) {
    /* Dynamic multi-field form: one Textarea per text field. File fields
       are collected into the shared `files` state (the existing upload
       pipeline) via a single picker below — file transport is flat, so
       we don't split it per field. */
    return (
      <div className="space-y-3">
        {textFields.map((field, index) => (
          <div key={`${field.key}-${index}`}>
            <label className="text-muted-foreground mb-1.5 block text-xs font-medium">
              {field.label || t("workflowLanding.testPanel.textInput", "Text Input")}
              {field.required && <span className="text-destructive ml-1">*</span>}
            </label>
            <Textarea
              className="min-h-[72px] resize-none text-sm"
              disabled={isRunning}
              value={fieldTextValues[field.key] ?? ""}
              placeholder={t(
                "workflowLanding.testPanel.inputPlaceholder",
                "Enter test input for the workflow..."
              )}
              onChange={(e) => handleFieldTextChange(field.key, e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
        ))}

        {hasFileField && (
          <div>
            <label className="text-muted-foreground mb-1.5 block text-xs font-medium">
              {t("workflowLanding.testPanel.filesOptional", "Files (Optional)")}
            </label>
            <Button
              className="w-full justify-center gap-2"
              disabled={isRunning}
              size="sm"
              variant="outline"
              onClick={() => setFilePickerOpen(true)}
            >
              <Paperclip className="size-3.5" />
              {t("workflowLanding.testPanel.selectFiles", "Click or drag to add files")}
            </Button>
            {files.length > 0 && (
              <div className="mt-2 space-y-1">
                {files.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="bg-muted/50 flex items-center justify-between gap-2 rounded px-2 py-1 text-xs"
                  >
                    <div className="flex min-w-0 items-center gap-1.5">
                      <FileText className="text-muted-foreground size-3 shrink-0" />
                      <span className="truncate">{file.name}</span>
                    </div>
                    <Button
                      className="-m-0.5 size-6 shrink-0 p-0"
                      disabled={isRunning}
                      size="icon"
                      variant="ghost"
                      onClick={() => removeFile(index)}
                    >
                      <X className="size-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {userInputSection}
      </div>
    )
  }

  return (
    <>
      {/* Text Input - only shown when text input is enabled (legacy) */}
      {allowTextInput && (
        <>
          <label className="text-muted-foreground mb-2 block text-xs font-medium">
            {t("workflowLanding.testPanel.textInput", "Text Input")}
          </label>
          <Textarea
            className="min-h-[80px] resize-none text-sm"
            disabled={isRunning}
            value={inputValue}
            placeholder={t(
              "workflowLanding.testPanel.inputPlaceholder",
              "Enter test input for the workflow..."
            )}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
        </>
      )}

      {/* File Upload - shown when files are allowed (legacy) */}
      {allowFiles && (
        <div className={cn(allowTextInput && "mt-3")}>
          <div className="mb-2 flex items-center justify-between">
            <label className="text-muted-foreground text-xs font-medium">
              {fileMode === "required"
                ? t("workflowLanding.testPanel.filesRequired", "Files (Required)")
                : t("workflowLanding.testPanel.filesOptional", "Files (Optional)")}
            </label>
          </div>
          <Button
            className="w-full justify-center gap-2"
            disabled={isRunning}
            size="sm"
            variant="outline"
            onClick={() => setFilePickerOpen(true)}
          >
            <Paperclip className="size-3.5" />
            {t("workflowLanding.testPanel.selectFiles", "Click or drag to add files")}
          </Button>
          {files.length > 0 && (
            <div className="mt-2 space-y-1">
              {files.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="bg-muted/50 flex items-center justify-between gap-2 rounded px-2 py-1 text-xs"
                >
                  <div className="flex min-w-0 items-center gap-1.5">
                    <FileText className="text-muted-foreground size-3 shrink-0" />
                    <span className="truncate">{file.name}</span>
                  </div>
                  <Button
                    className="-m-0.5 size-6 shrink-0 p-0"
                    disabled={isRunning}
                    size="icon"
                    variant="ghost"
                    onClick={() => removeFile(index)}
                  >
                    <X className="size-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {userInputSection && (
        <div className={cn((allowTextInput || allowFiles) && "mt-3")}>{userInputSection}</div>
      )}
    </>
  )
}
